import axios from 'axios';
import { camelizeKeys } from 'humps';
import { z } from 'zod';

import { ENV } from '@/config/env';
import { alliancesInWindow } from '@/utils/appOffers/alliances';
import { getRuleAppDiscount } from '@/utils/appOffers/appDiscount';
import { AppOffers } from './appOffers.types';

const TTL_MS = 5 * 60 * 1000;
const ERROR_TTL_MS = 60 * 1000;
const MAX_ENTRIES = 1000;

const PromotionCampaignSchema = z.object({
  campaign: z
    .object({
      country: z.string(),
      locationIds: z.array(z.number()),
    })
    .nullable(),
});

const LocationOffersSchema = z.object({
  currencySymbol: z.string().nullish(),
  delimiter: z.string().nullish(),
  smartPricingRule: z
    .object({
      appliesTo: z.enum(['all_business', 'services', 'providers']),
      windows: z.array(
        z.object({
          dayOfWeek: z.number(),
          startTime: z.string(),
          endTime: z.string(),
          value: z.number(),
          valueType: z.enum(['percentage', 'fixed']),
        }),
      ),
    })
    .nullish(),
});

type Campaign = z.infer<typeof PromotionCampaignSchema>['campaign'];
type LocationOffers = z.infer<typeof LocationOffersSchema> | null;

// marketplace-node's WAF rejects requests without a User-Agent.
const client = axios.create({
  baseURL: ENV.MARKETPLACE_NODE_URL,
  timeout: 1500,
  headers: { 'User-Agent': 'agendapro-ecommerce-frontend' },
});

// Lives while the Lambda stays warm; stores the promise so concurrent visits share a single request.
const cache = new Map<string, { expiresAt: number; value: Promise<unknown> }>();

const cached = <T>(key: string, load: () => Promise<T>, fallback: T): Promise<T> => {
  const now = Date.now();
  const hit = cache.get(key);

  if (hit && hit.expiresAt > now) {
    return hit.value as Promise<T>;
  }

  const entry: { expiresAt: number; value: Promise<T> } = { expiresAt: now + TTL_MS, value: Promise.resolve(fallback) };

  entry.value = load().catch(() => {
    entry.expiresAt = Date.now() + ERROR_TTL_MS;
    return fallback;
  });

  cache.delete(key);
  const oldest = cache.keys().next().value;

  if (cache.size >= MAX_ENTRIES && oldest) {
    cache.delete(oldest);
  }
  cache.set(key, entry);

  return entry.value;
};

const getCampaign = (slug: string): Promise<Campaign> =>
  cached(
    `campaign:${slug}`,
    async () => {
      const { data } = await client.get('/api/v1/promotion_campaign', {
        params: { slug },
        validateStatus: (status) => status === 200 || status === 400 || status === 404,
      });
      const parsed = PromotionCampaignSchema.safeParse(camelizeKeys(data));

      return parsed.success ? parsed.data.campaign : null;
    },
    null,
  );

const getLocationOffers = (locationId: number): Promise<LocationOffers> =>
  cached(
    `location:${locationId}`,
    async () => {
      const obfuscatedId = Buffer.from(String(locationId * 30)).toString('base64');
      const { data } = await client.get(`/api/v1/location_info/${obfuscatedId}`);
      const parsed = LocationOffersSchema.safeParse(camelizeKeys(data));

      return parsed.success ? parsed.data : null;
    },
    null,
  );

export const getAppOffers = async (countryCode: string, locationId: string): Promise<AppOffers | null> => {
  const id = Number(locationId);

  if (!ENV.MARKETPLACE_NODE_URL || !Number.isInteger(id)) {
    return null;
  }

  try {
    const country = countryCode.toLowerCase();
    const alliances = alliancesInWindow(country);
    const [location, ...campaigns] = await Promise.all([
      getLocationOffers(id),
      ...alliances.map((alliance) => getCampaign(alliance.slug)),
    ]);

    const adhered = alliances.filter((_, index) => {
      const campaign = campaigns[index];

      return campaign?.country.toLowerCase() === country && campaign.locationIds.includes(id);
    });
    const rule = location?.smartPricingRule;
    const discount = getRuleAppDiscount(rule);

    if (!adhered.length && !discount) {
      return null;
    }

    return {
      alliances: adhered.map(({ campaign, percentage, detailKey, appDownloadUrl }) => ({
        campaign,
        percentage,
        detailKey,
        appDownloadUrl,
      })),
      smartPricing:
        rule && discount
          ? {
              discount,
              selectedServicesOnly: rule.appliesTo === 'services',
              currencySymbol: location?.currencySymbol || '$',
              delimiter: location?.delimiter || '.',
            }
          : null,
    };
  } catch {
    return null;
  }
};

export const clearAppOffersCache = () => cache.clear();
